/**
 * Candidates as 9-bit masks: bit (d-1) set means digit d is still possible.
 *
 * Every technique in `techniques/` reads and writes these masks, so the whole
 * grader/hint engine is bit arithmetic rather than array churn.
 */

import { CELLS, EMPTY, type Board, type Digit } from "./grid";
import { CLASSIC, type ConstraintSet } from "./constraints";

/** All nine digits possible. */
export const ALL_MASK = 0b111111111;

/** cell -> bitmask of possible digits; 0 for cells that already hold a digit. */
export type Candidates = Int16Array;

export function maskOf(digit: Digit | number): number {
  return 1 << (digit - 1);
}

export function hasDigit(mask: number, digit: Digit | number): boolean {
  return (mask & maskOf(digit)) !== 0;
}

export function popcount(mask: number): number {
  let n = 0;
  let m = mask;
  while (m) {
    m &= m - 1;
    n++;
  }
  return n;
}

export function digitsOf(mask: number): Digit[] {
  const out: Digit[] = [];
  for (let d = 1; d <= 9; d++) if (mask & (1 << (d - 1))) out.push(d as Digit);
  return out;
}

/** The single digit in a one-bit mask. Throws if the mask is not a singleton. */
export function soleDigit(mask: number): Digit {
  if (mask === 0 || (mask & (mask - 1)) !== 0) {
    throw new Error(`soleDigit called on non-singleton mask ${mask.toString(2)}`);
  }
  return (31 - Math.clz32(mask) + 1) as Digit;
}

/**
 * Candidates implied purely by the placed digits -- no technique applied.
 *
 * A cell already holding a digit gets mask 0, which is what lets techniques
 * treat "has candidates" and "is unsolved" as the same test.
 */
export function computeCandidates(board: Board, cs: ConstraintSet = CLASSIC): Candidates {
  const candidates = new Int16Array(CELLS);
  for (let cell = 0; cell < CELLS; cell++) {
    if (board[cell] !== EMPTY) continue;
    let mask = ALL_MASK;
    for (const peer of cs.peers[cell]!) {
      const value = board[peer]!;
      if (value !== EMPTY) mask &= ~maskOf(value);
    }
    candidates[cell] = mask;
  }
  return candidates;
}

/** True if any constraint holds the same digit twice. */
export function hasConflict(board: Board, cs: ConstraintSet = CLASSIC): boolean {
  for (const constraint of cs.constraints) {
    let seen = 0;
    for (const cell of constraint.cells) {
      const value = board[cell]!;
      if (value === EMPTY) continue;
      const bit = maskOf(value);
      if (seen & bit) return true;
      seen |= bit;
    }
  }
  return false;
}

/** Cells that duplicate a digit within one of their constraints. */
export function conflictingCells(board: Board, cs: ConstraintSet = CLASSIC): Set<number> {
  const bad = new Set<number>();
  for (const constraint of cs.constraints) {
    const seenAt = new Map<number, number>();
    for (const cell of constraint.cells) {
      const value = board[cell]!;
      if (value === EMPTY) continue;
      const first = seenAt.get(value);
      if (first === undefined) {
        seenAt.set(value, cell);
      } else {
        bad.add(first);
        bad.add(cell);
      }
    }
  }
  return bad;
}

/** A complete, conflict-free board. */
export function isSolved(board: Board, cs: ConstraintSet = CLASSIC): boolean {
  for (let cell = 0; cell < CELLS; cell++) if (board[cell] === EMPTY) return false;
  return !hasConflict(board, cs);
}
